import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { api } from "@/helper/api";
import RoundedContentBox from "@/components/general/roundedContentBox";
import DropDown from "@/components/forms/inputs/dropDown";
import { DropDownAsync } from "@/components/forms/inputs/dropDownAsync";
import {
  getLmsDisplayText,
  getLmsManagerRequestConfig,
  getLmsManagerRequestParameters,
  LmsMode,
} from "@/components/lms/lms.utils";
import {
  getDashboardStatusOverviewFilterParams,
  getStatusOverviewFilterKindOptions,
  getStatusOverviewFilterParams,
  isInvalidCourseScopedFilterKind,
  resolveDepartmentCompanySubjectId,
  shouldShowAdminCompanySubjectSearch,
  type OverviewFilterKind,
} from "@/components/lms/lmsStatusOverviewScope";
import { getSubjectSearchCompanySubjectId } from "@/components/lms/lmsOverviewMetricsHelpers";
import DropDownSubjectSearch from "@/components/forms/inputs/dropDownSubjectSearch";
import { FormFieldLabel } from "@/components/forms/inputs/formFieldLabel";
import { RootState } from "@/components/state/store";
import LmsStatusOverviewDisplay, {
  LmsStatusOverviewStatusItem,
  LmsStatusOverviewViewMode,
} from "@/components/lms/lmsStatusOverviewDisplay";
import {
  areLmsDashboardFilterParamsEqual,
  type LmsDashboardStatusFilterParams,
} from "@/components/lms/useLmsDashboard";
import {
  EMPTY_STATUS_COUNTS,
  LMS_STATUS_OVERVIEW_CONFIGS,
  type LmsStatusOverviewStatusCounts,
  useLmsStatusOverviewCounts,
} from "@/components/lms/useLmsStatusOverviewCounts";
import {
  LmsStatusOverviewFilterOption,
  toCourseFilterOptions,
  useLmsStatusOverviewOptions,
} from "@/components/lms/useLmsStatusOverviewOptions";
import { loadManagerCompanySubjectId } from "@/components/lms/lmsManagerCompanyResolver";
import styles from "@/components/lms/lmsStatusOverviewCard.module.css";
import {
  getLmsSelectionBlockedMessage,
  LMS_START_VERTRAG_SELECTION_ENRICHMENT,
} from "@/components/lms/lmsQuickSearchEligibility";

type QueryParams = Record<string, string | number>;

export interface LmsAggregatedStatusOverview {
  clearError: () => void;
  error: any;
  isLoading: boolean;
  onFilterParamsChange: (params: QueryParams | null) => void;
  statusCountsFilterParams: LmsDashboardStatusFilterParams;
  statusCounts: LmsStatusOverviewStatusCounts;
}

interface Props {
  aggregatedDashboard?: LmsAggregatedStatusOverview;
  mode: LmsMode;
  participantId?: string | number;
  companySubjectId?: string;
  courseId?: string;
  dualDisplayMode?: boolean;
  dateFilterParams?: Record<string, string>;
}

const EMPTY_DATE_FILTER_PARAMS: Record<string, string> = {};

export default function LmsStatusOverviewCard({
  aggregatedDashboard,
  mode,
  participantId,
  companySubjectId,
  courseId,
  dualDisplayMode = false,
  dateFilterParams = EMPTY_DATE_FILTER_PARAMS,
}: Props) {
  const { t, i18n } = useTranslation();
  const isCourseScoped = Boolean(courseId);
  const loginState = useSelector((state: RootState) => state.login);
  const subjectSearch = useSelector((state: RootState) => state.subjectSearch);
  const selectedChildCompany = useSelector((state: RootState) => state.parent);
  const subdomain = window.location.hostname.split(".")[0];
  const [managerCompanySubjectId, setManagerCompanySubjectId] = useState("");
  const [viewMode, setViewMode] = useState<LmsStatusOverviewViewMode>("chart");
  const [filterKind, setFilterKind] = useState<OverviewFilterKind>("all");
  const [filterValue, setFilterValue] =
    useState<LmsStatusOverviewFilterOption | null>(null);
  const usesAggregatedDashboard = Boolean(aggregatedDashboard);

  const filterKindOptions = useMemo(
    () => getStatusOverviewFilterKindOptions(isCourseScoped, t),
    [isCourseScoped, t],
  );

  const subjectSearchCompanySubjectId = useMemo(
    () => getSubjectSearchCompanySubjectId(subjectSearch),
    [subjectSearch],
  );
  const departmentCompanySubjectId = useMemo(
    () =>
      resolveDepartmentCompanySubjectId({
        mode,
        companySubjectId,
        selectedChildCompanySubjectId: selectedChildCompany.subjectId
          ? String(selectedChildCompany.subjectId)
          : undefined,
        managerCompanySubjectId,
        subjectSearchCompanySubjectId,
      }),
    [
      companySubjectId,
      managerCompanySubjectId,
      mode,
      selectedChildCompany.subjectId,
      subjectSearchCompanySubjectId,
    ],
  );
  const showAdminCompanySubjectSearch = shouldShowAdminCompanySubjectSearch({
    mode,
    filterKind,
    companySubjectId,
  });
  const isParticipantLocked = Boolean(participantId);
  const participantSearchCompanySubjectId =
    companySubjectId || departmentCompanySubjectId;
  const managerRequestConfig = useMemo(
    () =>
      getLmsManagerRequestConfig(
        mode,
        selectedChildCompany.subjectId,
      ),
    [mode, selectedChildCompany.subjectId],
  );
  const managerRequestParameters = useMemo(
    () =>
      getLmsManagerRequestParameters(
        mode,
        selectedChildCompany.subjectId,
      ),
    [mode, selectedChildCompany.subjectId],
  );
  const shouldNotImpersonate = managerRequestConfig.shouldNotImpersonate ?? true;
  const scopedRequestParameters = useMemo<QueryParams>(
    () => ({
      ...managerRequestParameters,
      ...((companySubjectId || (mode === "manager" && departmentCompanySubjectId))
        ? {
            company_subject:
              companySubjectId ||
              (mode === "manager" ? departmentCompanySubjectId : undefined),
          }
        : {}),
    }),
    [companySubjectId, departmentCompanySubjectId, managerRequestParameters, mode],
  );
  const {
    departmentOptions,
    enrolledCourseOptions,
    isLoadingDepartments,
    isLoadingEnrolledCourses,
    languageOptions,
  } = useLmsStatusOverviewOptions({
    departmentCompanySubjectId,
    filterKind,
    scopedRequestParameters,
    shouldNotImpersonate,
    suggestEnrolledCourses: mode === "manager" && !isCourseScoped,
    t,
  });

  useEffect(() => {
    if (
      usesAggregatedDashboard ||
      mode !== "manager" ||
      companySubjectId ||
      selectedChildCompany.subjectId ||
      managerCompanySubjectId
    ) {
      return;
    }

    loadManagerCompanySubjectId(loginState.companyName)
      .then(setManagerCompanySubjectId)
      .catch(() => {
        setManagerCompanySubjectId("");
      });
  }, [
    companySubjectId,
    loginState.companyName,
    managerCompanySubjectId,
    mode,
    selectedChildCompany.subjectId,
    usesAggregatedDashboard,
  ]);

  useEffect(() => {
    if (isCourseScoped && isInvalidCourseScopedFilterKind(filterKind)) {
      setFilterKind("all");
      setFilterValue(null);
    }
  }, [filterKind, isCourseScoped, courseId]);

  useEffect(() => {
    if (filterKind === "department") {
      setFilterValue(null);
    }
  }, [departmentCompanySubjectId, filterKind]);

  const filterParams = useMemo(
    (): QueryParams | null => getStatusOverviewFilterParams({
      filterKind,
      selectedLanguage: filterKind === "language" ? filterValue : null,
      selectedCourse: filterKind === "course" ? filterValue : null,
      selectedParticipant: filterKind === "participant" ? filterValue : null,
      selectedDepartment: filterKind === "department" ? filterValue : null,
    }),
    [filterKind, filterValue],
  );

  const dashboardFilterParams = useMemo(
    () =>
      getDashboardStatusOverviewFilterParams({
        filterKind,
        selectedFilter: filterValue,
      }),
    [filterKind, filterValue],
  );

  const {
    clearOverviewError: clearLegacyOverviewError,
    isLoadingOverview: isLoadingLegacyOverview,
    overviewError: legacyOverviewError,
    statusCounts: legacyStatusCounts,
  } = useLmsStatusOverviewCounts({
    courseId,
    dateFilterParams,
    enabled: !usesAggregatedDashboard,
    filterParams,
    mode,
    participantId,
    scopedRequestParameters,
    shouldNotImpersonate,
  });

  const onDashboardFilterParamsChange =
    aggregatedDashboard?.onFilterParamsChange;

  useEffect(() => {
    onDashboardFilterParamsChange?.(dashboardFilterParams);
  }, [dashboardFilterParams, onDashboardFilterParamsChange]);

  const dashboardFilterHasNoValue = dashboardFilterParams === null;
  const dashboardStatusCountsAreCurrent = aggregatedDashboard
    ? !dashboardFilterHasNoValue &&
      areLmsDashboardFilterParamsEqual(
        dashboardFilterParams,
        aggregatedDashboard.statusCountsFilterParams,
      )
    : true;
  const clearOverviewError =
    aggregatedDashboard?.clearError ?? clearLegacyOverviewError;
  const isLoadingOverview = aggregatedDashboard
    ? !dashboardFilterHasNoValue &&
      !aggregatedDashboard.error &&
      (aggregatedDashboard.isLoading || !dashboardStatusCountsAreCurrent)
    : isLoadingLegacyOverview;
  const overviewError = aggregatedDashboard
    ? dashboardFilterHasNoValue
      ? null
      : aggregatedDashboard.error
    : legacyOverviewError;
  const statusCounts = aggregatedDashboard
    ? dashboardFilterHasNoValue || !dashboardStatusCountsAreCurrent
      ? EMPTY_STATUS_COUNTS
      : aggregatedDashboard.statusCounts
    : legacyStatusCounts;

  const loadCourseOptions = useCallback(
    (
      inputValue: string,
      finishCallback: (options: LmsStatusOverviewFilterOption[]) => void,
    ) => {
      const searchTerm = inputValue.trim();
      if (searchTerm.length < 2) {
        finishCallback([]);
        return;
      }

      if (mode === "administrator" || mode === "manager") {
        api.genericApiRequest({
          entity: "course",
          method: "get",
          shouldNotImpersonate,
          parametersToRender: {
            limit: 20,
            title__icontains: searchTerm,
            ...(mode === "manager" ? { status: "active" } : {}),
            ...scopedRequestParameters,
          },
          successHandler: (response: any) => {
            finishCallback(
              toCourseFilterOptions(response?.data?.results ?? []),
            );
          },
          failHandler: () => finishCallback([]),
        });
        return;
      }

      if (!participantId) {
        finishCallback([]);
        return;
      }

      api.genericApiRequest({
        entity: "enrolment",
        method: "get",
        shouldNotImpersonate: true,
        parametersToRender: {
          limit: 30,
          participant_id: participantId,
          course__title__icontains: searchTerm,
        },
        successHandler: (response: any) => {
          const uniqueByCourseId = new Map<string, LmsStatusOverviewFilterOption>();
          for (const enrolment of response?.data?.results ?? []) {
            const courseId = enrolment?.course?.id;
            const courseTitle = getLmsDisplayText(
              enrolment?.course?.title ?? enrolment?.course?.name,
            );
            if (!courseId || !courseTitle) {
              continue;
            }
            uniqueByCourseId.set(String(courseId), {
              value: String(courseId),
              label: courseTitle,
            });
          }
          finishCallback(Array.from(uniqueByCourseId.values()));
        },
        failHandler: () => finishCallback([]),
      });
    },
    [mode, participantId, scopedRequestParameters, shouldNotImpersonate],
  );

  const totalCount = useMemo(
    () =>
      statusCounts.not_started +
      statusCounts.in_progress +
      statusCounts.completed,
    [statusCounts],
  );

  const integerFormatter = useMemo(
    () =>
      new Intl.NumberFormat(
        i18n.language?.toLowerCase().startsWith("de") ? "de-DE" : "en-US",
      ),
    [i18n.language],
  );

  const percentFormatter = useMemo(
    () =>
      new Intl.NumberFormat(
        i18n.language?.toLowerCase().startsWith("de") ? "de-DE" : "en-US",
        { minimumFractionDigits: 0, maximumFractionDigits: 1 },
      ),
    [i18n.language],
  );

  const statusItems = useMemo<LmsStatusOverviewStatusItem[]>(
    () =>
      LMS_STATUS_OVERVIEW_CONFIGS.map((config) => {
        const count = statusCounts[config.key];
        return {
          ...config,
          label: t(config.key),
          count,
          percent: totalCount > 0 ? (count / totalCount) * 100 : 0,
        };
      }),
    [statusCounts, t, totalCount],
  );

  const chartSegments = useMemo(
    () =>
      statusItems.map((item) => ({
        id: item.key,
        label: item.label,
        value: item.count,
        color: item.color,
      })),
    [statusItems],
  );

  const shouldShowSecondaryFilter = filterKind === "language";

  const handleFilterKindChange = (nextFilterKind: OverviewFilterKind) => {
    setFilterKind(nextFilterKind);
    setFilterValue(null);
    clearOverviewError();
  };

  const handleParticipantSearchChange = (selectedOption: any) => {
    if (!selectedOption || Array.isArray(selectedOption)) {
      setFilterValue(null);
      return;
    }

    if (selectedOption.is_company === true || "companyId" in selectedOption) {
      setFilterValue(null);
      return;
    }

    setFilterValue({
      value: String(selectedOption.value),
      label: String(selectedOption.label ?? ""),
    });
  };

  const filterRowClassName = showAdminCompanySubjectSearch
    ? `${styles.filterRow} ${styles.filterRowThreeColumns}`
    : styles.filterRow;

  return (
    <RoundedContentBox className={styles.card}>
      <div className={styles.header}>
        <h3 className={styles.title}>{t("status")}</h3>
        {!dualDisplayMode && (
          <button
            type="button"
            className={styles.toggleButton}
            onClick={() =>
              setViewMode((currentMode) =>
                currentMode === "chart" ? "table" : "chart",
              )
            }
          >
            {viewMode === "chart"
              ? t("show_as_table")
              : t("show_as_pie_chart")
            }
          </button>
        )}
      </div>

      <div className={styles.body}>
        {!isParticipantLocked && (
          <div className={filterRowClassName}>
            <DropDown
              options={filterKindOptions}
              externalValue={filterKindOptions.find(
                (option) => option.value === filterKind,
              )}
              onChange={(value: any) => {
                const nextFilterKind =
                  (value?.value as OverviewFilterKind | undefined) ?? "all";
                handleFilterKindChange(nextFilterKind);
              }}
              isClearable={false}
              isSearchable={false}
              name="kaer-learning-overview-filter-kind"
              label={t("filter_by")}
            />

            {filterKind === "course" && !isCourseScoped ? (
              <DropDownAsync
                key={`overview-filter-${filterKind}`}
                options={[]}
                preExistingValue={filterValue}
                dataCallback={loadCourseOptions}
                defaultOptions={enrolledCourseOptions}
                showLoadingIndicator={isLoadingEnrolledCourses}
                onChange={(value: any) => {
                  if (!value || Array.isArray(value)) {
                    setFilterValue(null);
                    return;
                  }

                  setFilterValue({
                    value: String(value.value),
                    label: String(value.label ?? ""),
                  });
                }}
                isClearable={true}
                isSearchable={true}
                name="kaer-learning-overview-course-filter"
                label={t("specific_course")}
                placeholder={t("search_placeholder")}
              />
            ) : filterKind === "participant" ? (
              <div
                key={`overview-filter-${filterKind}`}
                className={styles.companySubjectSearch}
              >
                <FormFieldLabel
                  label={t("specific_participant")}
                  htmlFor="kaer-learning-overview-participant-filter"
                />
                <DropDownSubjectSearch
                  name="kaer-learning-overview-participant-filter"
                  subdomain={subdomain}
                  options={[]}
                  preExistingValue={filterValue}
                  onChange={handleParticipantSearchChange}
                  useGlobalState={false}
                  isClearable={true}
                  queryParameter="users"
                  selectionEnrichment={
                    LMS_START_VERTRAG_SELECTION_ENRICHMENT
                  }
                  getSelectionBlockedMessage={(selection) =>
                    getLmsSelectionBlockedMessage(selection, t)
                  }
                  allowSubjectSearchForNonAdmin={mode === "manager"}
                  subjectIdForFiltering={
                    participantSearchCompanySubjectId || undefined
                  }
                />
              </div>
            ) : filterKind === "department" ? (
              <>
                {showAdminCompanySubjectSearch && (
                  <div className={styles.companySubjectSearch}>
                    <FormFieldLabel
                      label={t("company")}
                      htmlFor="kaer-learning-overview-company-filter"
                    />
                    <DropDownSubjectSearch
                      name="kaer-learning-overview-company-filter"
                      subdomain={subdomain}
                      options={[]}
                      onChange={() => {
                        /* no-op */
                      }}
                      useGlobalState={true}
                      isClearable={true}
                      queryParameter="companies"
                      selectionEnrichment={
                        LMS_START_VERTRAG_SELECTION_ENRICHMENT
                      }
                      getSelectionBlockedMessage={(selection) =>
                        getLmsSelectionBlockedMessage(selection, t)
                      }
                    />
                  </div>
                )}
                <DropDown
                  key={`overview-filter-${filterKind}`}
                  options={departmentOptions}
                  externalValue={filterValue}
                  onChange={(value: LmsStatusOverviewFilterOption | null) =>
                    setFilterValue(value)
                  }
                  isClearable={true}
                  isSearchable={true}
                  showLoadingIndicator={isLoadingDepartments}
                  disabled={!departmentCompanySubjectId || isLoadingDepartments}
                  name="kaer-learning-overview-department-filter"
                  label={t("department")}
                  placeholder={t("search_placeholder")}
                />
              </>
            ) : shouldShowSecondaryFilter ? (
              <DropDown
                key={`overview-filter-${filterKind}`}
                options={languageOptions}
                externalValue={filterValue}
                onChange={(value: LmsStatusOverviewFilterOption | null) =>
                  setFilterValue(value)
                }
                isClearable={true}
                isSearchable={true}
                name="kaer-learning-overview-filter-value"
                label={t("language")}
              />
            ) : null}
          </div>
        )}
        <LmsStatusOverviewDisplay
          chartSegments={chartSegments}
          dualDisplayMode={dualDisplayMode}
          integerFormatter={integerFormatter}
          isLoadingOverview={isLoadingOverview}
          overviewError={overviewError}
          percentFormatter={percentFormatter}
          statusItems={statusItems}
          t={t}
          totalCount={totalCount}
          viewMode={viewMode}
        />
      </div>
    </RoundedContentBox>
  );
}
